import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Text,
  Icon,
  HStack,
  Button,
  Spinner,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  Portal,
} from "@chakra-ui/react";
import { FaFileAlt, FaSearch, FaTimes, FaTrash } from "react-icons/fa";
import { FaTableList } from "react-icons/fa6";
import { ArtifactProto } from "build-ts/chat_session";
import { NodeInsights } from "containers/Interfaces";
import DataFrameDisplayComponent from "components/data_frame/DataFrameDisplayComponent";
import DataFrameAnalysisComponent from "components/data_frame/DataFrameAnalysisComponent";
import { ArtifactType, artifactTypeFromJSON } from "build-ts/common";
import { DataFrameAnalysisProto_ParentHeader, KGDataFrameProto } from "build-ts/dataframe";
import { TbMathFunction, TbLayoutSidebarRightCollapseFilled } from "react-icons/tb";
import { ScheduledJobProto } from "build-ts/schedule";
import { IconType } from "react-icons";
import ScenarioArtifactCard from "./ScenarioArtifactCard";
import AnalysisOutputArtifactCard, { AnalysisOutputEntry } from "./AnalysisOutputArtifactCard";
import { IoLayers } from "react-icons/io5";
import DataTableComponent from "components/DataTableComponent";
import { LuWorkflow } from "react-icons/lu";

// ─── Types ────────────────────────────────────────────────────────────────────

type SelectedArtifactItem = {
  artifact?: ArtifactProto;
  nodeInsights?: NodeInsights;
  kgDataFrameData?: KGDataFrameProto;
  langgraphMessage?: string[];
  orderIndex: number;
  loading: boolean;
  analysisGroupId?: string;
  dataframeAnalysisView?: {
    activeTab?: string;
    selectedItemIndex?: number;
  };
};

interface ArtifactsProps {
  selectedArtifacts?: SelectedArtifactItem[];
  isLoadingArtifact?: boolean;
  onClosePanel?: () => void;
  onClearAll?: () => void;
  onRemoveArtifact?: (artifact: ArtifactProto) => void;
  /** Set of job header ids already persisted (used to show Saved vs Draft badge) */
  savedJobIds?: Set<string>;
  /** Called after a job is manually saved from the card */
  onJobSave?: (savedJob: ScheduledJobProto) => void;
  /** Analyst id passed to ScenarioArtifactCard for saving */
  analystId?: string;
  /** Called when a "Jump to source" button is clicked inside a DataFrameAnalysis card */
  onSourceClick?: (ph: DataFrameAnalysisProto_ParentHeader) => void;
  /** Signal to activate the tab for a specific orderIndex without moving it */
  activateArtifact?: { orderIndex: number; tick: number };
  /** All artifacts across all messages — shown in the browse menu */
  allArtifacts?: ArtifactProto[];
  /** Called when user picks an artifact from the browse menu */
  onArtifactClick?: (
    artifact: ArtifactProto,
    activeTab?: string,
    selectedItemIndex?: number
  ) => void;
  /** Whether the artifact panel is currently expanded to full width */
  isArtifactExpanded?: boolean;
  /** Called when the expand/collapse toggle is clicked inside an artifact header */
  onToggleExpand?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getArtifactTabKey(item: SelectedArtifactItem): string {
  const type = item.artifact ? artifactTypeFromJSON(item.artifact.artifact_type) : null;
  if (type === ArtifactType.ANALYSIS_OUTPUT && item.analysisGroupId) {
    return `group-${item.analysisGroupId}`;
  }
  return `item-${item.orderIndex}`;
}

function getArtifactLabel(item: ArtifactProto): string {
  const type = artifactTypeFromJSON(item.artifact_type);
  if (type === ArtifactType.KG_DATAFRAME)
    return item.kg_dataframe?.header?.name ?? "DataFrame";
  if (type === ArtifactType.SCENARIO_JOB)
    return item.scenario_job?.header?.name ?? "Scenario";
  if (type === ArtifactType.KG_OBSERVATION)
    return "KG Observation";
  if (type === ArtifactType.DATAFRAME_ANALYSIS)
    return item.dataframe_analysis?.header?.name ?? "DF Analysis";
  if (type === ArtifactType.ANALYSIS_OUTPUT)
    return "Analysis";
  return "Artifact";
}

function getArtifactIcon(artifact: ArtifactProto): IconType {
  const type = artifactTypeFromJSON(artifact.artifact_type);
  if (type === ArtifactType.KG_DATAFRAME)
    return FaTableList;
  if (type === ArtifactType.DATAFRAME_ANALYSIS) {
    return TbMathFunction;
  }
  if (type === ArtifactType.ANALYSIS_OUTPUT)
    return FaSearch;
  return LuWorkflow;
}


// ─── Component ────────────────────────────────────────────────────────────────

const Artifacts: React.FC<ArtifactsProps> = ({
  selectedArtifacts = [],
  isLoadingArtifact = false,
  onClosePanel,
  onClearAll,
  onRemoveArtifact,
  savedJobIds = new Set(),
  onJobSave,
  analystId,
  onSourceClick,
  activateArtifact,
  allArtifacts = [],
  onArtifactClick,
  isArtifactExpanded = false,
  onToggleExpand,
}) => {
  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);
  const tabStripRef = useRef<HTMLDivElement>(null);

  // Build a map of key → ArtifactProto[] for the browse menu.
  // ANALYSIS_OUTPUT: all outputs sharing the same job_id are grouped under one key
  //   so clicking opens all of them together (one grouped tab).
  // Every other type: single-item list
  const artifactMenuMap = useMemo((): Map<string, ArtifactProto[]> => {
    const map = new Map<string, ArtifactProto[]>();
    for (const a of allArtifacts) {
      let key: string;
      if (a.analysis_output) {
        // "ao:" prefix is required: analysis_output.job_id and scenario_job.header.id
        // are the same UUID, so without it they collide in the seen Set.
        key = `ao:${a.analysis_output.job_id ?? a.analysis_output.header?.id ?? "?"}`;
      } else if (a.scenario_job) {
        key = `job:${a.scenario_job.header?.id ?? "?"}`;
      } else if (a.dataframe_analysis) {
        key = a.dataframe_analysis.header?.id ?? "?";
      } else if (a.kg_dataframe) {
        key = a.kg_dataframe.header?.id ?? a.kg_dataframe.header?.name ?? "?";
      } else {
        key = String(a.artifact_type);
      }

      if (a.analysis_output) {
        // Accumulate all outputs for this job into one list
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(a);
      } else if (!map.has(key)) {
        // All other types
        map.set(key, [a]);
      }
    }
    return map;
  }, [allArtifacts]);

  // ── Build groups (ANALYSIS_OUTPUT items with shared groupId → one tab) ────
  const analysisOutputGroups: Array<{
    groupId: string;
    items: SelectedArtifactItem[];
    entries: AnalysisOutputEntry[];
    minOrderIndex: number;
  }> = [];
  const seenGroupIds = new Set<string>();

  for (const item of selectedArtifacts) {
    if (
      !item.artifact ||
      artifactTypeFromJSON(item.artifact.artifact_type) !== ArtifactType.ANALYSIS_OUTPUT ||
      !item.artifact.analysis_output
    ) continue;

    const gid = item.analysisGroupId ?? `solo-${item.orderIndex}`;
    if (!seenGroupIds.has(gid)) {
      seenGroupIds.add(gid);
      analysisOutputGroups.push({ groupId: gid, items: [], entries: [], minOrderIndex: Infinity });
    }
    const group = analysisOutputGroups.find(g => g.groupId === gid)!;
    group.items.push(item);
    group.entries.push({ output: item.artifact.analysis_output, onRemove: () => onRemoveArtifact?.(item.artifact!) });
    if (item.orderIndex < group.minOrderIndex) group.minOrderIndex = item.orderIndex;
  }

  // ── Build unified tab list ─────────────────────────────────────────────────
  type ArtifactTab = {
    key: string;
    label: string;
    icon: IconType;
    sortKey: number;
    onClose: () => void;
  };

  const allTabs = useMemo((): ArtifactTab[] => {
    const tabs: ArtifactTab[] = [];

    // ANALYSIS_OUTPUT group tabs
    for (const group of analysisOutputGroups) {
      tabs.push({
        key: `group-${group.groupId}`,
        label: `Analysis (${group.entries.length})`,
        icon: FaSearch,
        sortKey: group.minOrderIndex,
        onClose: () => group.entries.forEach(e => e.onRemove()),
      });
    }

    // (non-ANALYSIS_OUTPUT)
    for (const item of selectedArtifacts) {
      const type = item.artifact ? artifactTypeFromJSON(item.artifact.artifact_type) : null;
      if (type === ArtifactType.ANALYSIS_OUTPUT) continue;
      tabs.push({
        key: `item-${item.orderIndex}`,
        label: getArtifactLabel(item.artifact!),
        icon: getArtifactIcon(item.artifact!),
        sortKey: item.orderIndex,
        onClose: () => item.artifact && onRemoveArtifact?.(item.artifact),
      });
    }

    // Chronological order (oldest tab on left)
    tabs.sort((a, b) => a.sortKey - b.sortKey);
    return tabs;
  }, [selectedArtifacts]);

  // ── Auto-switch to the tab of the most-recently activated artifact ─────────
  const newestOrderIndex = useMemo(
    () => selectedArtifacts.reduce((max, item) => Math.max(max, item.orderIndex), -1),
    [selectedArtifacts],
  );

  useEffect(() => {
    if (selectedArtifacts.length === 0) {
      setActiveTabKey(null);
      return;
    }
    const newest = selectedArtifacts.find(item => item.orderIndex === newestOrderIndex);
    if (newest) {
      setActiveTabKey(getArtifactTabKey(newest));
    }
  }, [newestOrderIndex]);

  // Activate a specific tab on re-click (without moving it)
  useEffect(() => {
    if (!activateArtifact) return;
    const item = selectedArtifacts.find(i => i.orderIndex === activateArtifact.orderIndex);
    if (item) {
      setActiveTabKey(getArtifactTabKey(item));
    }
  }, [activateArtifact?.tick]);

  // If the active tab's artifact was removed, fall back to the rightmost remaining tab
  useEffect(() => {
    if (!activeTabKey) return;
    const stillExists = allTabs.some(t => t.key === activeTabKey);
    if (!stillExists && allTabs.length > 0) {
      setActiveTabKey(allTabs[allTabs.length - 1]?.key);
    }
  }, [allTabs, activeTabKey]);

  // Scroll the tab strip so the active tab is visible
  useEffect(() => {
    if (!activeTabKey || !tabStripRef.current) return;

    const container = tabStripRef.current;
    const activeEl = container.querySelector<HTMLElement>(
      `[data-artifact-tab-id="${activeTabKey}"]`
    );

    if (!activeEl) return;

    activeEl.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeTabKey, allTabs]);

  const hasAnything = selectedArtifacts.length > 0 || isLoadingArtifact;

  // ── Render the content for the currently active tab ───────────────────────
  const renderActiveContent = () => {
    if (!activeTabKey) return null;

    // ANALYSIS_OUTPUT group tab
    if (activeTabKey.startsWith("group-")) {
      const groupId = activeTabKey.slice("group-".length);
      const group = analysisOutputGroups.find(g => g.groupId === groupId);
      if (!group) return null;
      return (
        <Box key={groupId} width="100%" overflow="hidden" p={1}>
          <AnalysisOutputArtifactCard
            key={groupId}
            entries={group.entries}
            isSelected={true}
            defaultExpanded={true}
          />
        </Box>
      );
    }

    const orderIndex = parseInt(activeTabKey.slice("item-".length), 10);
    const item = selectedArtifacts.find(i => i.orderIndex === orderIndex);
    if (!item) return null;

    const artifactType = item.artifact ? artifactTypeFromJSON(item.artifact.artifact_type) : null;

    if (artifactType === ArtifactType.SCENARIO_JOB && item.artifact?.scenario_job) {
      const scheduleJob = item.artifact?.scenario_job;
      if (!scheduleJob) return null;
      const jobId = scheduleJob.header?.id ?? "";
      const isSavedInAnalyst = !!jobId && savedJobIds.has(jobId);
      return (
        <Box
          key={item.orderIndex}
          w="100%"
          h="100%"
          minH={0}
          overflow="hidden"
          p={1}
          display="flex"
          flexDir="column"
        >
          <ScenarioArtifactCard
            key={item.orderIndex}
            scheduleJob={scheduleJob}
            analystId={analystId}
            isSavedInAnalyst={isSavedInAnalyst}
            isSelected={true}
            defaultExpanded={true}
            onRemove={() => onRemoveArtifact?.(item.artifact!)}
            onSave={onJobSave}
            isExpanded={isArtifactExpanded}
            onExpandToggle={onToggleExpand}
          />
        </Box>
      );
    }

    if (artifactType === ArtifactType.KG_OBSERVATION && item.nodeInsights) {
      return (
        <Box key={item.orderIndex} width="100%" overflow="hidden" p={1}>
          <DataTableComponent
            key={item.orderIndex}
            name={"KG Observation"}
            observationData={item.nodeInsights}
            onCloseTable={() => onRemoveArtifact?.(item.artifact!)}
            isSelected={true}
          />
        </Box>
      );
    }

    if (artifactType === ArtifactType.KG_DATAFRAME && item.kgDataFrameData) {
      return (
        <Box
          key={item.orderIndex}
          w="100%"
          flex="1"
          minH={0}
          display="flex"
          flexDir="column"
          overflow="hidden"
          p={1}
        >
          <DataFrameDisplayComponent
            key={item.orderIndex}
            name={item.kgDataFrameData?.header?.name || "KG DataFrame"}
            data={item.kgDataFrameData}
            onClose={() => onRemoveArtifact?.(item.artifact!)}
            isSelected={true}
            isExpanded={isArtifactExpanded}
            onExpandToggle={onToggleExpand}
          />
        </Box>
      );
    }

    if (artifactType === ArtifactType.DATAFRAME_ANALYSIS && item.artifact?.dataframe_analysis) {
      return (
        <Box
          key={item.orderIndex}
          w="100%"
          flex="1"
          minH={0}
          display="flex"
          flexDir="column"
          overflow="hidden"
          p={1}
        >
          <DataFrameAnalysisComponent
            key={item.orderIndex}
            data={item.artifact.dataframe_analysis}
            onClose={() => onRemoveArtifact?.(item.artifact!)}
            onSourceClick={onSourceClick}
            initialActiveTab={item.dataframeAnalysisView?.activeTab}
            initialSelectedItemIndex={item.dataframeAnalysisView?.selectedItemIndex}
            isExpanded={isArtifactExpanded}
            onExpandToggle={onToggleExpand}
          />
        </Box>
      );
    }

    return (
      <Box p={4}>
        <Text color="gray.500" fontSize="sm">Unable to display artifact</Text>
      </Box>
    );
  };

  return (
    <Box height="100%" display="flex" flexDirection="column" bg="white" overflow="hidden">

      {/* ── Tab-strip Header ──────────────────────────────────────────────── */}
      <HStack
        spacing={0}
        align="center"
        px={1}
        py={0.5}
        borderBottom="1px solid"
        borderColor="gray.400"
        bg="gray.50"
        flexShrink={0}
      >
        {/* Left: browse-menu icon + label */}
        <Tooltip
          label={artifactMenuMap.size > 0 ? "Show all artifacts in this chat" : "No artifacts in this session yet"}
          shouldWrapChildren
          placement="left-end"
        >
          <Menu placement="bottom-start" strategy="fixed" gutter={4}>
            <MenuButton
              as={Button}
              variant="ghost"
              size="sm"
              px={1}
              py={1}
              flexShrink={0}
              isDisabled={artifactMenuMap.size === 0}
              aria-label={`Open session artifacts menu (${artifactMenuMap.size} in list)`}
              _hover={artifactMenuMap.size > 0 ? { bg: "purple.50" } : undefined}
            >
              <HStack spacing={1} align="center">
                <Icon as={IoLayers} boxSize={5} color="purple.600" aria-hidden />
                <Text
                  as="span"
                  fontSize="xs"
                  fontWeight="700"
                  color="purple.700"
                  mb={0}
                  lineHeight="1"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                  mr={1}
                >
                  {artifactMenuMap.size}
                </Text>
              </HStack>
            </MenuButton>
            <Portal>
              <MenuList maxH="240px" overflowY="auto" zIndex={1500} py={1} fontSize="sm">
                {Array.from(artifactMenuMap.values()).map((group, i) => (
                  <MenuItem
                    key={i}
                    onClick={() => group.forEach(a => onArtifactClick?.(a))}
                    fontSize="sm"
                  >
                    {getArtifactLabel(group[0])}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        </Tooltip>
        {/* Middle: scrollable tabs */}
        <Box
          ref={tabStripRef as React.RefObject<HTMLDivElement>}
          flex="1"
          overflowX="auto"
          css={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
          h="100%"
        >
          <HStack spacing={1} align="flex-end" minW="min-content" py={0.5} pr={1}>
            {allTabs.map((tab) => {
              const isActive = activeTabKey === tab.key;
              return (
                <HStack
                  key={tab.key}
                  role="tab"
                  data-artifact-tab-id={tab.key}
                  aria-selected={isActive}
                  maxW="200px"
                  minW={0}
                  flexShrink={0}
                  spacing={2}
                  pl={2}
                  pr={0.5}
                  py={0.5}
                  borderRadius="md"
                  bg={isActive ? "purple.50" : "white"}
                  borderWidth="1px"
                  borderColor={isActive ? "purple.700" : "gray.400"}
                  boxShadow={isActive ? "0 1px 2px rgba(107, 70, 193, 0.12)" : "none"}
                  cursor="pointer"
                  onClick={() => setActiveTabKey(tab.key)}
                  _hover={{
                    bg: isActive ? "purple.50" : "gray.50",
                    borderColor: isActive ? "purple.300" : "gray.300",
                  }}
                  sx={{
                    "&:hover .artifact-tab-close, &:focus-within .artifact-tab-close": {
                      opacity: 1,
                      pointerEvents: "auto",
                    },
                  }}
                >
                  <Icon
                    as={tab.icon}
                    boxSize={3}
                    color={isActive ? "purple.600" : "gray.500"}
                    flexShrink={0}
                  />
                  <Text
                    fontSize="xs"
                    fontWeight={isActive ? "700" : "500"}
                    color={isActive ? "purple.800" : "gray.600"}
                    mb={0}
                    flex={1}
                    minW={0}
                    noOfLines={1}
                    title={tab.label}
                    textAlign="left"
                  >
                    {tab.label}
                  </Text>
                  <IconButton
                    className="artifact-tab-close"
                    size="xs"
                    variant="ghost"
                    aria-label={`Close ${tab.label}`}
                    icon={<Icon as={FaTimes} boxSize={3} />}
                    color="gray.600"
                    minW="24px"
                    h="24px"
                    flexShrink={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      tab.onClose();
                    }}
                    opacity={isActive ? 1 : 0}
                    pointerEvents={isActive ? "auto" : "none"}
                    transition="opacity 0.12s ease"
                    _hover={{ color: "gray.700", bg: "blackAlpha.100" }}
                  />
                </HStack>
              );
            })}
          </HStack>
        </Box>

        {/* Right: clear-all + collapse */}
        <HStack spacing={0} flexShrink={0} h="100%" px={1}>
          {selectedArtifacts.length > 0 && (
            <Button
              leftIcon={<Icon as={FaTrash} boxSize="2.5" />}
              size="xs"
              colorScheme="red"
              color="gray.500"
              _hover={{ color: "red.500", bg: "red.50" }}
              onClick={onClearAll}
              fontSize="xs"
              iconSpacing={1}
              variant="ghost"
            >
              Clear
            </Button>
          )}
          <Button
            size="xs"
            variant="ghost"
            color="gray.500"
            _hover={{ color: "gray.700", bg: "gray.100" }}
            onClick={onClosePanel}
            p={1}
          >
            <Icon as={TbLayoutSidebarRightCollapseFilled} boxSize="5" />
          </Button>
        </HStack>
      </HStack>

      {/* ── Loading Indicator ─────────────────────────────── */}
      {isLoadingArtifact && (
        <Box
          p={2}
          position="absolute"
          top="15%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex="25"
          bg="green.100"
          borderRadius={3}
          boxShadow="md"
        >
          <HStack spacing={2}>
            <Spinner size="sm" color="green.600" thickness="2px" />
            <Text mb={0} fontSize="xs" fontWeight="semibold" color="green.700">
              Loading artifact...
            </Text>
          </HStack>
        </Box>
      )}

      {/* ── Content: flex column + minH=0 so children can fill & scroll in place, not the whole bar */}
      <Box
        flex="1"
        minH={0}
        display="flex"
        flexDirection="column"
        overflowY="auto"
        p={0}
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 transparent",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "3px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#A0AEC0" },
        }}
      >
        {!hasAnything ? (
          <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={6}>
            <Box textAlign="center">
              <Icon as={FaFileAlt} boxSize={12} color="gray.400" mb={4} />
              <Text color="gray.600" fontSize="lg" mb={2}>
                No Artifacts Selected
              </Text>
              <Text fontSize="sm" color="gray.500">
                Click on artifacts in the chat to view them here
              </Text>
            </Box>
          </Box>
        ) : (
          renderActiveContent()
        )}
      </Box>
    </Box>
  );
};

export default Artifacts;
